/**
 * Loyalty (points-as-currency MVP) types.
 *
 * All config is keyed on the currently selected business location
 * (`businessInfo.id` — the salon uuid the app already sends as
 * `business_location_id` on other writes, e.g. PUT /staff).
 *
 * NOTE: backend PR2 (config endpoints) and PR5 (client account / adjust /
 * opt-in) are not deployed yet — every consumer must tolerate 404/failure.
 */

import { formatCurrency } from "../utils/cashDrawerUtils";

/** How clients earn points; drives which spend earn-rules are seeded. */
export type LoyaltyPreset = "both" | "retail" | "service";

export type PointsExpirationPolicy = "none" | "calendar_year" | "rolling";

export interface LoyaltySettings {
  /** Present only when a configured row exists (vs backend defaults). */
  id?: number | string | null;
  business_location_id?: number | string;
  enabled: boolean;
  preset: LoyaltyPreset;
  deduct_redemption_from_commission: boolean;
  points_expiration_policy: PointsExpirationPolicy;
  /** 365 for `rolling`; null/ignored for other policies. */
  points_expiration_days: number | null;
  auto_enroll_all_clients?: boolean;
  created_dt?: string | null;
  updated_dt?: string | null;
}

export type LoyaltyEarnRuleType =
  | "service_spend"
  | "retail_spend"
  | "rebooking"
  | "nth_visit"
  | "referral"
  | "custom";

export interface LoyaltyEarnRule {
  id: number | string;
  /** PUT/DELETE paths match on uuid, not the numeric id. */
  uuid: string;
  rule_type: LoyaltyEarnRuleType;
  points: number;
  /** Spend rules: dollars per `points` increment (e.g. 1.00). */
  per_amount?: number | string | null;
  /** nth_visit: award every N visits. */
  interval_count?: number | null;
  /** Lifetime cap per client; null/absent = unlimited (1 = once-only,
   *  e.g. first appointment). May be absent on older backend builds. */
  max_awards_per_client?: number | null;
  label?: string | null;
  is_active?: boolean;
}

export interface CreateLoyaltyEarnRuleRequest {
  rule_type: LoyaltyEarnRuleType;
  points: number;
  per_amount?: number;
  interval_count?: number;
  /** Positive int cap; omit for unlimited. */
  max_awards_per_client?: number;
  label?: string;
}

export type UpdateLoyaltyEarnRuleRequest = Partial<
  Omit<CreateLoyaltyEarnRuleRequest, "max_awards_per_client">
> & {
  is_active?: boolean;
  /** PUT merges omitted keys into the existing rule, so clearing the cap
   *  requires an explicit null. */
  max_awards_per_client?: number | null;
};

/** Milestone reward ("500 points = $5 off entire transaction"). */
export interface LoyaltyReward {
  id: number | string;
  /** PUT/DELETE paths match on uuid, not the numeric id. */
  uuid: string;
  name: string;
  points_cost: number;
  cash_value: number | string;
  is_active?: boolean;
}

export interface CreateLoyaltyRewardRequest {
  name: string;
  points_cost: number;
  cash_value: number;
}

export type UpdateLoyaltyRewardRequest = Partial<CreateLoyaltyRewardRequest> & {
  is_active?: boolean;
};

export interface UpdateLoyaltySettingsRequest {
  enabled: boolean;
  preset: LoyaltyPreset;
  deduct_redemption_from_commission: boolean;
  points_expiration_policy: PointsExpirationPolicy;
  points_expiration_days: number | null;
  auto_enroll_all_clients?: boolean;
}

export interface LoyaltyActivityCountMetric {
  count: number;
  total_clients_ever?: number;
}

export interface LoyaltyActivityPointsMetric {
  points: number;
  clients: number;
}

export interface LoyaltyLiabilityPointsMetric {
  points: number;
  clients: number;
}

export interface LoyaltyLiabilityValueMetric {
  amount: number;
  clients: number;
}

/** GET /business-locations/{uuid}/loyalty/summary — sibling PR #500 shape.
 *  `expired_points` was added in a follow-up backend PR; it is optional so an
 *  older backend build (pre-deploy) still parses. Consumers must render
 *  nothing rather than crash when the field is absent. */
export interface LoyaltyLocationSummaryResponse {
  launched_dt: string | null;
  date_range: { start_date: string; end_date: string };
  activity: {
    newly_enrolled_clients: LoyaltyActivityCountMetric;
    earned_points: LoyaltyActivityPointsMetric;
    redeemed_points: LoyaltyActivityPointsMetric;
    added_points: LoyaltyActivityPointsMetric;
    removed_points: LoyaltyActivityPointsMetric;
    expired_points?: LoyaltyActivityPointsMetric;
  };
  liability: {
    unused_points: LoyaltyLiabilityPointsMetric;
    unused_point_value: LoyaltyLiabilityValueMetric;
  };
}

export interface ClientLoyaltyAccount {
  points_balance: number;
  /** [BE-pending: lifetime_points exposure] — may be absent from GET. */
  lifetime_points?: number | null;
  lifetime_spend?: number | string | null;
  opted_in: boolean;
}

export interface ClientLoyaltyLedgerRow {
  id: number | string;
  event_type: string;
  delta: number;
  created_dt?: string;
  note?: string | null;
}

export interface ClientLoyaltyResponse {
  account: ClientLoyaltyAccount | null;
  ledger?: ClientLoyaltyLedgerRow[];
}

export interface AdjustClientLoyaltyPointsRequest {
  /** NUMERIC business_locations.id — the backend rejects the uuid with 400. */
  business_location_id: number;
  delta: number;
  note: string;
}

export interface SetClientLoyaltyOptInRequest {
  /** NUMERIC business_locations.id — the backend rejects the uuid with 400. */
  business_location_id: number;
  opted_in: boolean;
}

/** GET /transactions/{uuid}/loyalty/available-rewards — rows arrive inside an
 *  `{available_rewards: [...]}` envelope; only milestones the client can
 *  afford; applied_value = min(cash_value, remaining sale total). */
export interface AvailableLoyaltyReward {
  reward_id: number;
  name: string;
  points_cost: number;
  cash_value: number | string;
  applied_value: number | string;
}

/** Checkout stages one or more milestone picks locally, then commits the
 *  sum at Charge as a flat-rate `{points}` redemption. The backend accepts
 *  either a single `{reward_id}` (legacy per-pick eager path) or a flat-rate
 *  `{points}` body; the admin portal only sends the flat-rate form from the
 *  checkout commit. Server posts a method='Loyalty' tender for the
 *  redemption's applied value. */
export type RedeemLoyaltyRewardRequest =
  | { reward_id: number | string }
  | { points: number };

/** GET /transactions/{uuid}/loyalty/preview-earn — points the transaction
 *  will earn on final settlement, per the location's active earn rules. Both
 *  the top-level `expected_points` and the `breakdown` keys are optional /
 *  partial; consumers must tolerate missing fields (endpoint may not be
 *  deployed yet, and older builds may omit rules that don't apply). */
export interface LoyaltyEarnPreviewBreakdownEntry {
  points: number;
  /** For spend-based rules: dollar amount that produced `points`. */
  paid_amount?: number | string;
}

export interface LoyaltyEarnPreviewBreakdown {
  service_spend?: LoyaltyEarnPreviewBreakdownEntry;
  retail_spend?: LoyaltyEarnPreviewBreakdownEntry;
  rebooking?: LoyaltyEarnPreviewBreakdownEntry;
  nth_visit?: LoyaltyEarnPreviewBreakdownEntry;
  custom?: LoyaltyEarnPreviewBreakdownEntry;
}

export interface LoyaltyEarnPreviewResponse {
  expected_points: number;
  breakdown?: LoyaltyEarnPreviewBreakdown;
}

/** POST /transactions/{uuid}/loyalty/redeem — server posts a method='Loyalty'
 *  payments tender for applied_value that settles against the transaction. */
export interface RedeemLoyaltyRewardResponse {
  payment_id: number;
  payment_uuid?: string;
  points_spent: number;
  applied_value: number | string;
  new_points_balance: number;
  deduct_redemption_from_commission?: boolean;
  commission_reduction?: number;
}

/** True when GET settings returned a real configured row (vs defaults). */
export function isConfiguredLoyaltySettings(
  settings: Partial<LoyaltySettings> | null | undefined,
): settings is LoyaltySettings {
  if (!settings || typeof settings !== "object") return false;
  return settings.id != null || settings.created_dt != null;
}

/** Human copy for the dashboard earn-rate sentence (split per-type rates). */
export function presetEarnSentence(
  preset: LoyaltyPreset,
  servicesPoints: number = 10,
  productsPoints: number = 10,
  perAmount: number = 1,
): string {
  const dollars = formatCurrency(perAmount);
  switch (preset) {
    case "retail":
      return `Clients earn ${productsPoints} points for every ${dollars} spent on products only.`;
    case "service":
      return `Clients earn ${servicesPoints} points for every ${dollars} spent on services only.`;
    default:
      return servicesPoints === productsPoints
        ? `Clients earn ${servicesPoints} points for every ${dollars} spent on services or products.`
        : `Clients earn ${servicesPoints} points per ${dollars} on services and ${productsPoints} points per ${dollars} on products.`;
  }
}
